import { useEffect, useId, useMemo, useRef } from 'react';
import { useEditor } from '@tiptap/react';
import type { Editor } from '@tiptap/react';
import type { RichTextEditorProps } from '@/shared/ui/rich-text-editor/types';
import { createExtensions, normaliseHtml } from './schema';

/** The props the mount actually depends on. */
type ControlledEditorArgs = Pick<
  RichTextEditorProps,
  'value' | 'onChange' | 'label' | 'isDisabled' | 'isReadOnly' | 'isInvalid' | 'errorMessage'
>;

export type ControlledEditor = {
  editor: Editor | null;
  /** Id of the error paragraph, for the surface's `aria-describedby`. */
  errorId: string;
};

/**
 * Brings the document in line with a controlled value, unless it already is.
 *
 * (a) The document already holds the controlled value — nothing to do. This is
 *     also the case for a host that echoes an emit.
 * (b) The value is the document we last emitted, but the document has moved on
 *     since (a late or deferred echo). Adopting it would revert just-typed
 *     characters and reset the selection, so it is ignored.
 * (c) Anything else is the host's authority: an external replacement, a host
 *     that clamps what it stores, or a host that refused an edit. The value is
 *     adopted without being emitted back — `emitUpdate: false` is the
 *     suppress-once that stops a rewrite loop.
 */
function syncDocument(editor: Editor, value: string | null, lastEmitted: string | null): void {
  const next = normaliseHtml(value ?? '');
  const current = normaliseHtml(editor.getHTML(), editor.isEmpty);
  if (next === current || next === lastEmitted) return;
  editor.commands.setContent(next, { emitUpdate: false });
}

/**
 * Mounts the Tiptap editor and keeps it in step with a controlled HTML value.
 *
 * Four effects, each with one job:
 *
 * 1. `setEditable` — read-only and disabled both stop editing. The second
 *    argument is `false` because v3's `setEditable` emits an update by default,
 *    and a host would then receive a document it never edited on mount.
 * 2. `aria-label` — the label is the editing surface's accessible name, so it
 *    follows the prop rather than the first render.
 * 3. Convergence (`syncDocument`) — the incoming value is compared with the
 *    last value EMITTED, not with the live document. A host that stores what it
 *    is given therefore never triggers a write-back, and a host that rewrites
 *    or refuses an edit still wins on the next render.
 * 4. The invalid surface — `aria-invalid` and the error's id as the accessible
 *    description, the same contract `Input`/`Textarea` carry.
 */
export function useControlledEditor({
  value,
  onChange,
  label,
  isDisabled = false,
  isReadOnly = false,
  isInvalid = false,
  errorMessage,
}: ControlledEditorArgs): ControlledEditor {
  const onChangeRef = useRef(onChange);
  const extensions = useMemo(() => createExtensions(), []);
  const errorId = useId();
  /** The last document handed to the host. `null` until the first edit. */
  const lastEmittedRef = useRef<string | null>(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor({
    extensions,
    content: normaliseHtml(value ?? ''),
    immediatelyRender: false,
    editable: !isDisabled && !isReadOnly,
    editorProps: {
      attributes: {
        role: 'textbox',
        'aria-multiline': 'true',
        'aria-label': label,
      },
    },
    onUpdate: ({ editor: current }) => {
      const html = normaliseHtml(current.getHTML(), current.isEmpty);
      // Recorded before the host is called: the host may re-render with this
      // exact string, and the convergence effect reads the ref on that render.
      lastEmittedRef.current = html;
      onChangeRef.current(html);
    },
  });

  useEffect(() => {
    if (!editor) return;
    const editable = !isDisabled && !isReadOnly;
    if (editor.isEditable !== editable) editor.setEditable(editable, false);
  }, [editor, isDisabled, isReadOnly]);

  useEffect(() => {
    if (!editor) return;
    editor.view.dom.setAttribute('aria-label', label);
  }, [editor, label]);

  /**
   * Deliberately dependency-free: drift can appear without `value` changing
   * (a refused edit), so the check runs on every render.
   */
  useEffect(() => {
    if (editor) syncDocument(editor, value, lastEmittedRef.current);
  });

  useEffect(() => {
    if (!editor) return;
    const dom = editor.view.dom;
    if (isInvalid) dom.setAttribute('aria-invalid', 'true');
    else dom.removeAttribute('aria-invalid');
    if (errorMessage) dom.setAttribute('aria-describedby', errorId);
    else dom.removeAttribute('aria-describedby');
  }, [editor, isInvalid, errorMessage, errorId]);

  return { editor, errorId };
}
